(() => {
  if (window.AcLottieManager) {
    window.AcLottieManager.refresh?.();
    return;
  }
  const lifecycle = new AbortController();
  const controllers = new Set();
  const motionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
  const on = (target, type, callback, options = {}) => target.addEventListener(type, callback, { ...options, signal: lifecycle.signal });
  const config = document.querySelector('script[data-ac-lottie-runtime-src]');
  const runtimeSrc = config?.dataset.acLottieRuntimeSrc || '';
  const workerRuntimeSrc = config?.dataset.acLottieWorkerRuntimeSrc || '';
  const assets = Object.freeze({
    'close-arrow': config?.dataset.acLottieCloseArrowSrc || '',
    'footer-discount': config?.dataset.acLottieFooterDiscountSrc || '',
    'marker-red': config?.dataset.acLottieMarkerSrc || '',
  });
  // Public text actions opt in by type, including Shopify's light-DOM payment link.
  const actionSelector = '[data-ac-lottie-action], .ac-icon-button--close, :is(.ac-action--marker, .ac-button--text, .ac-button--brush, .ac-button--outline, a.ac-text-link, .shopify-payment-button__more-options):not(.ac-icon-button, [data-ac-marker="none"])';
  const DESTROY_DELAY = 150;
  const SCROLL_HOVER_SETTLE_DELAY = 350;
  const actionStates = new WeakMap();
  const pendingHoverOwners = new Set();
  const choiceHoverStates = new WeakMap();
  const dataCache = new Map();
  const markerDataCache = new WeakMap();
  const retainedHeaderMarkers = new Set();
  const MAX_RETAINED_HEADER_MARKERS = 3;
  let nextMarkerAnchorId = 0;
  let activeAction = null;
  let keyboardInteraction = false;
  let runtimePromise = null;
  let workerRuntimePromise = null;
  let workerUnavailable = false;
  let lastPointerMove = -Infinity;
  let lastScroll = -Infinity;
  let pointerX = -1;
  let pointerY = -1;
  let pageHidden = false;

  const reducedMotion = () => (
    document.body.dataset.acMotion === 'reduced'
    || document.body.classList.contains('ac-motion-reduced')
    || motionQuery.matches
  );

  const finePointer = () => window.matchMedia('(hover: hover) and (pointer: fine)').matches;
  const assetUrl = (name) => assets[name] || '';

  const loadMainRuntime = () => {
    if (window.lottie?.loadAnimation) return Promise.resolve(window.lottie);
    if (runtimePromise) return runtimePromise;
    if (!runtimeSrc) return Promise.reject(new Error('Lottie runtime URL is unavailable'));

    runtimePromise = new Promise((resolve, reject) => {
      const script = document.createElement('script');
      script.src = runtimeSrc;
      script.async = true;
      script.dataset.acLottieRuntime = 'true';
      script.addEventListener('load', () => {
        if (window.lottie?.loadAnimation) resolve(window.lottie);
        else reject(new Error('Lottie runtime did not expose loadAnimation'));
      }, { once: true });
      script.addEventListener('error', () => reject(new Error('Lottie runtime failed to load')), { once: true });
      document.head.append(script);
    }).catch((error) => {
      runtimePromise = null;
      throw error;
    });

    return runtimePromise;
  };

  const loadWorkerRuntime = () => {
    if (window.acLottieWorker?.loadAnimation) return Promise.resolve(window.acLottieWorker);
    if (workerRuntimePromise) return workerRuntimePromise;
    if (!workerRuntimeSrc) return Promise.reject(new Error('Lottie worker runtime URL is unavailable'));

    workerRuntimePromise = new Promise((resolve, reject) => {
      const script = document.createElement('script');
      script.src = workerRuntimeSrc;
      script.async = true;
      script.dataset.acLottieWorkerRuntime = 'true';
      script.addEventListener('load', () => {
        if (window.acLottieWorker?.loadAnimation) resolve(window.acLottieWorker);
        else reject(new Error('Lottie worker runtime did not expose loadAnimation'));
      }, { once: true });
      script.addEventListener('error', () => reject(new Error('Lottie worker runtime failed to load')), { once: true });
      document.head.append(script);
    }).catch((error) => {
      workerRuntimePromise = null;
      workerUnavailable = true;
      throw error;
    });

    return workerRuntimePromise;
  };

  const loadRuntime = (workerEnabled) => {
    const canUseWorker = workerEnabled && workerRuntimeSrc && !workerUnavailable && typeof Worker === 'function'
      && typeof OffscreenCanvas === 'function'
      && typeof HTMLCanvasElement.prototype.transferControlToOffscreen === 'function';
    return canUseWorker ? loadWorkerRuntime().catch(loadMainRuntime) : loadMainRuntime();
  };

  const loadData = (path) => {
    if (!path) return Promise.reject(new Error('Lottie animation URL is unavailable'));
    if (dataCache.has(path)) return dataCache.get(path);

    const request = fetch(path, { credentials: 'same-origin' })
      .then((response) => {
        if (!response.ok) throw new Error(`Lottie animation request failed (${response.status})`);
        return response.json();
      })
      .catch((error) => {
        dataCache.delete(path);
        throw error;
      });
    dataCache.set(path, request);
    return request;
  };

  const cloneData = (value) => (typeof structuredClone === 'function'
    ? structuredClone(value) : JSON.parse(JSON.stringify(value)));

  const prepareAnimationData = (value, asset, worker) => {
    if (asset !== 'marker-red') return cloneData(value);
    const color = getComputedStyle(document.documentElement).getPropertyValue('--ac-action-marker-color').trim();
    let cached = markerDataCache.get(value);
    if (!cached || cached.color !== color) {
      const canvas = document.createElement('canvas');
      canvas.width = canvas.height = 1;
      const context = canvas.getContext('2d');
      context.fillStyle = color;
      context.fillRect(0, 0, 1, 1);
      const rgb = [...context.getImageData(0, 0, 1, 1).data].slice(0, 3).map(channel => channel / 255);
      const data = cloneData(value);
      const recolor = (shapes) => {
        for (const shape of shapes || []) {
          if (shape.ty === 'gr') recolor(shape.it);
          else if (shape.ty === 'fl' && shape.c && !shape.c.a) shape.c.k = [...rgb, shape.c.k[3] ?? 1];
        }
      };
      for (let index = 0; index < (data.layers || []).length; index += 1) {
        if (!data.layers[index + 1]?.tt) recolor(data.layers[index].shapes);
      }
      cached = {color, data};
      markerDataCache.set(value, cached);
    }
    return worker ? cached.data : cloneData(cached.data);
  };

  class AcLottieController {
    #host;
    #options;
    #animation = null;
    #frameRequest = 0;
    #generation = 0;
    #destroyed = false;
    #ready;
    #cancelMount;
    #finishPlayback;
    #finishInterpolation;

    constructor(host, options) {
      this.#host = host;
      this.#options = options;
      controllers.add(this);
      this.#ready = this.#mount();
    }

    get ready() { return this.#ready; }

    get destroyed() { return this.#destroyed; }

    get connected() { return this.#host.isConnected; }

    async #mount() {
      let animation = null;
      let workerAnimation = false;
      try {
        const [lottie, animationData] = await Promise.all([
          loadRuntime(this.#options.worker),
          this.#options.cacheData === false ? Promise.resolve(null) : loadData(this.#options.path),
        ]);
        if (this.#destroyed || !this.#host.isConnected) throw new Error('Lottie host was disconnected');

        workerAnimation = lottie === window.acLottieWorker;
        animation = lottie.loadAnimation({
          container: this.#host,
          renderer: 'canvas',
          loop: false,
          autoplay: false,
          ...(animationData ? { animationData: prepareAnimationData(animationData, this.#options.asset, workerAnimation) } : { path: this.#options.path }),
          rendererSettings: {
            clearCanvas: true,
            progressiveLoad: this.#options.progressiveLoad ?? true,
            hideOnTransparent: this.#options.hideOnTransparent ?? false,
            preserveAspectRatio: this.#options.preserveAspectRatio || 'xMidYMid meet',
          },
        });
        this.#animation = animation;
        if (workerAnimation) {
          // The Canvas Worker proxy leaves currentFrame at its initial value.
          // Track its frame events so reverse and interrupted playback start at the visible frame.
          animation.addEventListener('enterFrame', ({ currentTime }) => {
            if (Number.isFinite(currentTime)) animation.currentFrame = currentTime;
          });
        }
        if (this.#options.subframe === false) animation.setSubframe?.(false);

        await new Promise((resolve, reject) => {
          let settled = false;
          const cleanup = () => {
            window.clearTimeout(timeout);
            animation.removeEventListener('DOMLoaded', finish);
            animation.removeEventListener('data_ready', finish);
            animation.removeEventListener('data_failed', fail);
            this.#cancelMount = null;
          };
          const finish = () => {
            if (settled) return;
            settled = true;
            cleanup();
            resolve();
          };
          const fail = () => {
            if (settled) return;
            settled = true;
            cleanup();
            reject(new Error('Lottie animation data failed to load'));
          };
          const timeout = window.setTimeout(fail, 12000);
          this.#cancelMount = fail;
          animation.addEventListener('DOMLoaded', finish);
          animation.addEventListener('data_ready', finish);
          animation.addEventListener('data_failed', fail);
        });

        if (this.#destroyed) {
          throw new Error('Lottie controller was destroyed');
        }
        animation.goToAndStop(0, true);
        this.#host.dataset.acLottieReady = 'true';
        this.#host.dispatchEvent(new CustomEvent('ac:lottie-ready', { bubbles: true }));
        return animation;
      } catch (error) {
        if (workerAnimation && !this.#destroyed && this.#host.isConnected) {
          workerUnavailable = true;
          animation?.destroy();
          this.#animation = null;
          this.#host.replaceChildren();
          return this.#mount();
        }
        controllers.delete(this);
        if (!this.#destroyed) {
          animation?.destroy();
          this.#animation = null;
          this.#host.dataset.acLottieError = 'true';
          this.#host.dispatchEvent(new CustomEvent('ac:lottie-error', { bubbles: true, detail: { error } }));
        }
        throw error;
      }
    }

    #cancelFrameInterpolation() {
      if (this.#frameRequest) cancelAnimationFrame(this.#frameRequest);
      this.#frameRequest = 0;
      this.#finishInterpolation?.();
      this.#finishInterpolation = null;
      this.#finishPlayback?.();
      this.#finishPlayback = null;
      this.#animation?.pause?.();
    }

    async #animateFrames(animation, targetFrame, duration, generation) {
      const startFrame = Number.isFinite(animation.currentFrame) ? animation.currentFrame : 0;
      if (duration <= 0 || Math.abs(targetFrame - startFrame) < 0.5) {
        animation.goToAndStop(targetFrame, true);
        return;
      }

      await new Promise((resolve) => {
        this.#finishInterpolation = resolve;
        const start = performance.now();
        const tick = (time) => {
          if (this.#destroyed || generation !== this.#generation) {
            resolve();
            return;
          }
          const elapsed = Math.min(1, (time - start) / (duration * 1000));
          const eased = 1 - ((1 - elapsed) ** 3);
          const frame = startFrame + ((targetFrame - startFrame) * eased);
          animation.goToAndStop(this.#options.subframe === false ? Math.round(frame) : frame, true);
          if (elapsed < 1) this.#frameRequest = requestAnimationFrame(tick);
          else {
            this.#frameRequest = 0;
            this.#finishInterpolation = null;
            resolve();
          }
        };
        this.#frameRequest = requestAnimationFrame(tick);
      });
    }

    async animateTo(progress, duration = 0.6) {
      const generation = ++this.#generation;
      this.#cancelFrameInterpolation();
      const animation = await this.#ready;
      if (this.#destroyed || generation !== this.#generation) return;

      const finalFrame = Math.max(0, (animation.totalFrames || 1) - 1);
      const progressFrame = Math.max(0, Math.min(1, progress)) * finalFrame;
      const targetFrame = this.#options.subframe === false ? Math.round(progressFrame) : progressFrame;
      await this.#animateFrames(animation, targetFrame, reducedMotion() ? 0 : duration, generation);
    }

    async #playDirection(direction, duration) {
      const generation = ++this.#generation;
      this.#cancelFrameInterpolation();
      const animation = await this.#ready;
      if (this.#destroyed || generation !== this.#generation) return;

      duration = reducedMotion() ? 0 : duration;
      const finalFrame = Math.max(0, (animation.totalFrames || 1) - 1);
      const targetFrame = direction > 0 ? finalFrame : 0;
      const currentFrame = Number.isFinite(animation.currentFrame) ? animation.currentFrame : 0;
      if (duration <= 0 || Math.abs(targetFrame - currentFrame) < 0.5) {
        animation.goToAndStop(targetFrame, true);
        return;
      }

      if (
        typeof animation.play !== 'function'
        || typeof animation.setDirection !== 'function'
        || typeof animation.setSpeed !== 'function'
      ) {
        await this.#animateFrames(animation, targetFrame, duration, generation);
        return;
      }

      const frameRate = Number(animation.frameRate) || 0;
      const fullDuration = frameRate > 0 ? (animation.totalFrames || 1) / frameRate : duration;
      animation.setSpeed(duration > 0 ? fullDuration / duration : 1);
      animation.setDirection(direction);

      await new Promise((resolve) => {
        let settled = false;
        const finish = () => {
          if (settled) return;
          settled = true;
          window.clearTimeout(timeout);
          animation.removeEventListener?.('complete', finish);
          this.#finishPlayback = null;
          resolve();
        };
        const timeout = window.setTimeout(finish, Math.max(100, (duration * 1000) + 250));
        this.#finishPlayback = finish;
        animation.addEventListener('complete', finish);
        animation.play();
      });
    }

    playForward(duration = 0.5) { return this.#playDirection(1, duration); }

    playReverse(duration = 0.5) { return this.#playDirection(-1, duration); }

    pause() {
      if (this.#destroyed) return;
      this.#generation += 1;
      this.#cancelFrameInterpolation();
    }

    reset() {
      this.pause();
      this.#animation?.goToAndStop(0, true);
    }

    destroy() {
      if (this.#destroyed) return;
      this.#destroyed = true;
      controllers.delete(this);
      this.#generation += 1;
      this.#cancelFrameInterpolation();
      this.#cancelMount?.();
      this.#animation?.destroy();
      this.#animation = null;
      this.#host.replaceChildren();
      delete this.#host.dataset.acLottieReady;
    }
  }

  const create = (host, options = {}) => {
    if (!(host instanceof HTMLElement)) throw new TypeError('A Lottie host element is required');
    const path = options.path || assetUrl(options.asset);
    if (!path) throw new TypeError('A Lottie animation path or registered asset is required');
    return new AcLottieController(host, { ...options, path });
  };

  const findOwner = (target) => target instanceof Element ? target.closest(actionSelector) : null;

  const resolveAsset = (owner) => {
    const requested = owner.dataset.acLottieAction;
    if (requested && assets[requested]) return requested;
    if (owner.classList.contains('ac-icon-button--close')) return 'close-arrow';
    return 'marker-red';
  };

  const findOrCreateHost = (owner, asset) => {
    const existing = owner.querySelector('[data-ac-lottie-host]');
    if (existing) return existing;

    let label = owner.querySelector('.ac-action__label');
    if (!label && asset === 'marker-red') {
      // Decorate bare text without replacing the control, its icons or click handlers.
      const textNodes = [...owner.childNodes].filter(node => node.nodeType === Node.TEXT_NODE && node.textContent.trim());
      if (textNodes.length) {
        label = document.createElement('span');
        label.className = 'ac-action__label';
        owner.insertBefore(label, textNodes[0]);
        label.append(...textNodes);
      }
    }
    label ||= owner;
    const host = document.createElement('span');
    host.className = `ac-action__lottie ac-action__lottie--${asset === 'close-arrow' ? 'close' : 'marker'}`;
    host.dataset.acLottieHost = '';
    host.dataset.acLottieGenerated = 'true';
    host.setAttribute('aria-hidden', 'true');
    label.append(host);
    return host;
  };

  const getState = (owner) => {
    let state = actionStates.get(owner);
    if (state) return state;
    state = {
      controller: null,
      generation: 0,
      host: null,
      insideFocus: false,
      insidePointer: false,
      destroyTimer: 0,
      hoverTimer: 0,
      anchor: null,
      positionFrame: 0,
      portalController: null,
      anchorName: '',
    };
    actionStates.set(owner, state);
    return state;
  };

  const clearPendingHover = (owner, state) => {
    window.clearTimeout(state.hoverTimer);
    state.hoverTimer = 0;
    pendingHoverOwners.delete(owner);
  };

  const isUnavailable = (owner) => (
    owner.matches(':disabled, [aria-disabled="true"], [aria-busy="true"]')
    || owner.classList.contains('is-loading')
  );

  const destroyAction = (owner, retain = false) => {
    delete owner.dataset.acLottieActive;
    const state = actionStates.get(owner);
    if (!state) return;
    state.generation += 1;
    window.clearTimeout(state.destroyTimer);
    state.destroyTimer = 0;
    clearPendingHover(owner, state);
    state.insideFocus = false;
    state.insidePointer = false;
    if (retain && !pageHidden && !reducedMotion() && owner.isConnected
      && owner.closest('[data-ac-lottie-portal-root]') && resolveAsset(owner) === 'marker-red'
      && state.controller && !state.controller.destroyed && state.host?.dataset.acLottieReady === 'true'
      && !state.portalController) {
      // Keep a few ready Header players on their own anchors so repeat hovers
      // do not reconstruct the same 75 vector shape groups.
      state.controller.reset();
      retainedHeaderMarkers.delete(owner);
      retainedHeaderMarkers.add(owner);
      if (activeAction === owner) activeAction = null;
      while (retainedHeaderMarkers.size > MAX_RETAINED_HEADER_MARKERS) {
        destroyAction(retainedHeaderMarkers.values().next().value);
      }
      return;
    }
    retainedHeaderMarkers.delete(owner);
    if (state.positionFrame) cancelAnimationFrame(state.positionFrame);
    state.positionFrame = 0;
    state.portalController?.abort();
    state.portalController = null;
    state.anchor?.remove();
    state.anchor = null;
    state.controller?.destroy();
    state.controller = null;
    if (state.host?.dataset.acLottieGenerated === 'true') state.host.remove();
    state.host = null;
    if (activeAction === owner) activeAction = null;
  };

  let movingAnchorSupported;
  const supportsMovingAnchor = () => {
    if (movingAnchorSupported !== undefined) return movingAnchorSupported;
    if (!window.CSS?.supports('position-anchor: --ac-lottie-marker')
      || !CSS.supports('left: anchor(left)') || !CSS.supports('top: anchor(top)')
      || !CSS.supports('width: anchor-size(width)') || !CSS.supports('height: anchor-size(height)')) {
      return (movingAnchorSupported = false);
    }
    // Some implementations accept the syntax but do not follow a transformed
    // anchor. Probe the movement once before choosing the native path.
    const probe = document.createElement('div');
    const moving = document.createElement('div');
    const anchor = document.createElement('span');
    const portal = document.createElement('span');
    probe.style.cssText = 'position:fixed;left:20px;top:20px;opacity:0;pointer-events:none;z-index:-1';
    moving.style.cssText = 'position:absolute;left:0;top:0;transform:translate(11px,7px)';
    anchor.style.cssText = 'display:block;width:9px;height:7px;anchor-name:--ac-lottie-transform-probe';
    portal.style.cssText = 'position:fixed;position-anchor:--ac-lottie-transform-probe;left:anchor(left);top:anchor(top);width:anchor-size(width);height:anchor-size(height)';
    moving.append(anchor);
    probe.append(moving, portal);
    document.body.append(probe);
    const aligned = () => {
      const a = anchor.getBoundingClientRect();
      const p = portal.getBoundingClientRect();
      return Math.abs(a.x - p.x) < .25 && Math.abs(a.y - p.y) < .25
        && Math.abs(a.width - p.width) < .25 && Math.abs(a.height - p.height) < .25;
    };
    const initial = aligned();
    moving.style.transform = 'translate(37px,29px)';
    movingAnchorSupported = initial && aligned();
    probe.remove();
    return movingAnchorSupported;
  };

  // Opt-in decoration layer: retain the label's geometry without inheriting its blend.
  const positionPortal = (owner, state) => {
    const root = owner.matches('.ac-action--marker, [data-ac-lottie-portal]')
      ? owner.closest('[data-ac-lottie-portal-root]') : null;
    if (!root || !state.host) return;
    state.anchor = state.host.cloneNode(false);
    state.anchor.removeAttribute('data-ac-lottie-host');
    state.anchor.removeAttribute('data-ac-lottie-generated');
    state.anchor.setAttribute('data-ac-lottie-anchor', '');
    state.host.replaceWith(state.anchor);
    state.host.classList.add('ac-action__lottie--portal');
    root.append(state.host);
    // Let the browser follow the moving marker without a read/write loop when
    // CSS anchor positioning is available. Older browsers use the measured
    // rectangle path below.
    if (supportsMovingAnchor()) {
      state.anchorName ||= '--ac-lottie-marker-' + (++nextMarkerAnchorId);
      state.anchor.style.anchorName = state.anchorName;
      Object.assign(state.host.style, {
        positionAnchor: state.anchorName,
        left: 'anchor(left)', top: 'anchor(top)',
        width: 'anchor-size(width)', height: 'anchor-size(height)',
      });
      return;
    }
    state.portalController = new AbortController();
    const { signal } = state.portalController;
    const motionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
    let previous = '';
    const schedule = () => {
      if (!state.positionFrame && !signal.aborted) state.positionFrame = requestAnimationFrame(position);
    };
    const position = () => {
      state.positionFrame = 0;
      if (motionQuery.matches || document.body.classList.contains('ac-motion-reduced')
        || !owner.isConnected || !state.anchor?.isConnected || !state.host?.isConnected) {
        destroyAction(owner);
        return;
      }
      const rect = state.anchor.getBoundingClientRect();
      const key = `${rect.x},${rect.y},${rect.width},${rect.height}`;
      if (key !== previous) {
        previous = key;
        Object.assign(state.host.style, {
          left: `${rect.x}px`, top: `${rect.y}px`,
          width: `${rect.width}px`, height: `${rect.height}px`,
        });
      }
      // Track moving anchors; a settled hover must not read layout forever.
      if (root.getAnimations({ subtree: true }).some((animation) => animation.playState === 'running')) schedule();
    };
    root.addEventListener('transitionrun', schedule, { signal });
    root.addEventListener('transitionend', schedule, { signal });
    root.addEventListener('animationstart', schedule, { signal });
    window.addEventListener('resize', schedule, { passive: true, signal });
    motionQuery.addEventListener('change', schedule, { signal });
    document.addEventListener('scroll', schedule, { capture: true, passive: true, signal });
    const observer = new MutationObserver((records) => {
      if (records.some(({ target }) => target !== state.anchor && !state.host?.contains(target))) schedule();
    });
    observer.observe(root, { subtree: true, attributes: true, attributeFilter: ['class', 'style', 'hidden', 'data-shell-state'] });
    observer.observe(document.body, { attributes: true, attributeFilter: ['class'] });
    const resizeObserver = new ResizeObserver(schedule);
    resizeObserver.observe(owner);
    signal.addEventListener('abort', () => {
      observer.disconnect();
      resizeObserver.disconnect();
    }, { once: true });
    position();
  };

  const activateAction = async (owner) => {
    if (pageHidden || !owner?.isConnected || reducedMotion() || !finePointer() || isUnavailable(owner)) return;
    if (activeAction && activeAction !== owner) destroyAction(activeAction, true);

    const state = getState(owner);
    retainedHeaderMarkers.delete(owner);
    window.clearTimeout(state.destroyTimer);
    state.destroyTimer = 0;
    const asset = resolveAsset(owner);
    const path = assetUrl(asset);
    if (!path) return;
    const token = ++state.generation;
    activeAction = owner;
    owner.dataset.acLottieActive = 'true';

    try {
      if (!state.controller || state.controller.destroyed) {
        state.host = findOrCreateHost(owner, asset);
        positionPortal(owner, state);
        state.controller = create(state.host, {
          asset,
          worker: asset === 'marker-red',
          hideOnTransparent: true,
          preserveAspectRatio: 'none',
        });
      }
      await state.controller.playForward(Number(owner.dataset.acLottieDuration) || 0.5);
    } catch {
      if (token === state.generation) destroyAction(owner);
    }
  };

  const deactivateAction = async (owner) => {
    delete owner.dataset.acLottieActive;
    const state = actionStates.get(owner);
    if (!state || state.insideFocus || state.insidePointer || !state.controller) return;
    const token = ++state.generation;
    const controller = state.controller;
    try {
      await controller.playReverse(Number(owner.dataset.acLottieDuration) || 0.5);
    } catch {
      // The static action remains usable when an animation request fails.
    }
    if (token !== state.generation || state.insideFocus || state.insidePointer) return;
    state.destroyTimer = window.setTimeout(() => {
      if (token !== state.generation || state.insideFocus || state.insidePointer) return;
      destroyAction(owner, true);
    }, DESTROY_DELAY);
  };

  const onPointerOver = (event) => {
    if (pageHidden) return;
    if (!finePointer()) return;
    const owner = findOwner(event.target);
    if (!owner || owner.contains(event.relatedTarget)) return;
    const state = getState(owner);
    state.insidePointer = true;
    pointerX = event.clientX;
    pointerY = event.clientY;
    const now = performance.now();
    if (now - lastScroll < 180 && now - lastPointerMove > 100) {
      clearPendingHover(owner, state);
      pendingHoverOwners.add(owner);
      const activateWhenSettled = () => {
        state.hoverTimer = 0;
        if (!state.insidePointer || !owner.isConnected) {
          pendingHoverOwners.delete(owner);
          return;
        }
        const remaining = SCROLL_HOVER_SETTLE_DELAY - (performance.now() - lastScroll);
        if (remaining > 0) {
          state.hoverTimer = window.setTimeout(activateWhenSettled, remaining);
          return;
        }
        pendingHoverOwners.delete(owner);
        if (!owner.contains(document.elementFromPoint(pointerX, pointerY))) return;
        activateAction(owner);
      };
      state.hoverTimer = window.setTimeout(activateWhenSettled, SCROLL_HOVER_SETTLE_DELAY);
      return;
    }
    activateAction(owner);
  };

  const onPointerOut = (event) => {
    const owner = findOwner(event.target);
    if (!owner || owner.contains(event.relatedTarget)) return;
    const state = getState(owner);
    state.insidePointer = false;
    clearPendingHover(owner, state);
    deactivateAction(owner);
  };

  const onPointerMove = (event) => {
    lastPointerMove = performance.now();
    pointerX = event.clientX;
    pointerY = event.clientY;
    if (!pendingHoverOwners.size) return;
    for (const owner of pendingHoverOwners) {
      if (!owner.contains(event.target)) continue;
      const state = actionStates.get(owner);
      if (!state?.insidePointer) break;
      clearPendingHover(owner, state);
      activateAction(owner);
      break;
    }
  };

  const onScroll = () => { lastScroll = performance.now(); };

  const onPointerDown = () => {
    keyboardInteraction = false;
  };

  const onKeyDown = (event) => {
    if (event.metaKey || event.ctrlKey || event.altKey) return;
    keyboardInteraction = true;
  };

  const onFocusIn = (event) => {
    const owner = findOwner(event.target);
    if (!owner || !keyboardInteraction) return;
    const state = getState(owner);
    state.insideFocus = true;
    clearPendingHover(owner, state);
    activateAction(owner);
  };

  const onFocusOut = (event) => {
    const owner = findOwner(event.target);
    if (!owner || owner.contains(event.relatedTarget)) return;
    const state = getState(owner);
    state.insideFocus = false;
    deactivateAction(owner);
  };

  const destroyWithin = (root) => {
    if (!(root instanceof Element)) return;
    if (root.matches(actionSelector)) destroyAction(root);
    for (const owner of root.querySelectorAll(actionSelector)) destroyAction(owner);
    if (root.matches('.ac-action-choice[data-ac-lottie-hover]')) destroyChoiceHover(root);
    for (const choice of root.querySelectorAll('.ac-action-choice[data-ac-lottie-hover]')) destroyChoiceHover(choice);
  };

  const destroyChoiceHover = (choice) => {
    const state = choiceHoverStates.get(choice);
    if (state) {
      state.generation++;
      state.controller?.destroy();
      choiceHoverStates.delete(choice);
    }
    delete choice.dataset.acChoiceHovering;
    delete choice.dataset.acChoiceError;
  };

  const choiceInput = (choice) => {
    const input = choice.previousElementSibling;
    return input instanceof HTMLInputElement && input.matches('input[type="radio"], input[type="checkbox"]') ? input : null;
  };

  const getChoiceHoverState = (choice) => {
    let state = choiceHoverStates.get(choice);
    if (!state) {
      state = { controller: null, generation: 0, insidePointer: false, insideFocus: false };
      choiceHoverStates.set(choice, state);
    }
    return state;
  };

  const clearChoiceHover = async (choice) => {
    const state = choiceHoverStates.get(choice);
    if (!state) return;
    const input = choiceInput(choice);
    // Native checked styling owns selection; no hover animation may outlive it.
    if (!input || input.checked || input.disabled || reducedMotion()) {
      destroyChoiceHover(choice);
      return;
    }
    if (state.insidePointer || state.insideFocus) return;
    const controller = state.controller;
    const generation = ++state.generation;
    delete choice.dataset.acChoiceHovering;
    if (!controller) return;
    try {
      await controller.playReverse(Number(choice.dataset.acLottieDuration) || 0.5);
    } catch {
      // Hover decoration may fail without affecting the native input.
    }
    if (generation !== state.generation || state.insidePointer || state.insideFocus
      || choiceHoverStates.get(choice) !== state) return;
    destroyChoiceHover(choice);
  };

  const activateChoiceHover = async (choice) => {
    const input = choiceInput(choice);
    if (!input || input.checked || input.disabled || choice.dataset.unavailable === 'true' || reducedMotion()) return;
    const state = getChoiceHoverState(choice);
    if (!state.controller || state.controller.destroyed) {
      const host = choice.querySelector('[data-ac-lottie-choice-hover-host]');
      const asset = choice.dataset.acLottieHover;
      if (!(host instanceof HTMLElement) || !assetUrl(asset)) return;
      state.controller = create(host, { asset, worker: asset === 'marker-red', hideOnTransparent: true, preserveAspectRatio: 'none' });
    }
    const generation = ++state.generation;
    delete choice.dataset.acChoiceError;
    choice.dataset.acChoiceHovering = 'true';
    try {
      await state.controller.playForward(Number(choice.dataset.acLottieDuration) || 0.5);
    } catch {
      if (generation === state.generation && choiceHoverStates.get(choice) === state) {
        destroyChoiceHover(choice);
        choice.dataset.acChoiceError = 'true';
      }
    }
  };

  // Existing callers refresh after product partial updates. Selection itself is native CSS.
  const syncChoiceHovers = (root = document) => {
    const selector = '.ac-action-choice[data-ac-lottie-hover]';
    const choices = root instanceof Element && root.matches(selector) ? [root] : root.querySelectorAll(selector);
    for (const choice of choices) {
      const input = choiceInput(choice);
      if (!input || input.checked || input.disabled || reducedMotion()) destroyChoiceHover(choice);
    }
  };

  const findChoice = (target) => {
    if (!(target instanceof Element)) return null;
    const choice = target.closest('.ac-action-choice[data-ac-lottie-hover]');
    if (choice) return choice;
    if (target instanceof HTMLInputElement) {
      const label = target.nextElementSibling;
      if (label?.matches('.ac-action-choice[data-ac-lottie-hover]')) return label;
    }
    return null;
  };

  const onChoicePointerOver = (event) => {
    if (!finePointer()) return;
    const choice = findChoice(event.target);
    if (!choice || choice.contains(event.relatedTarget)) return;
    const state = getChoiceHoverState(choice);
    state.insidePointer = true;
    activateChoiceHover(choice);
  };

  const onChoicePointerOut = (event) => {
    const choice = findChoice(event.target);
    if (!choice || choice.contains(event.relatedTarget)) return;
    const state = getChoiceHoverState(choice);
    state.insidePointer = false;
    clearChoiceHover(choice);
  };

  const onChoiceFocusIn = (event) => {
    if (!keyboardInteraction) return;
    const choice = findChoice(event.target);
    if (!choice) return;
    const state = getChoiceHoverState(choice);
    state.insideFocus = true;
    activateChoiceHover(choice);
  };

  const onChoiceFocusOut = (event) => {
    const choice = findChoice(event.target);
    if (!choice || choice.contains(event.relatedTarget)) return;
    const state = getChoiceHoverState(choice);
    state.insideFocus = false;
    clearChoiceHover(choice);
  };

  let lastReduced = reducedMotion();
  const syncMotion = () => {
    const reduced = reducedMotion();
    if (lastReduced === reduced) return;
    lastReduced = reduced;
    if (reduced) {
      if (activeAction) destroyAction(activeAction);
      destroyWithin(document.documentElement);
      controllers.forEach((controller) => controller.pause());
    } else syncChoiceHovers();
  };
  const motionObserver = new MutationObserver(syncMotion);
  motionObserver.observe(document.body, { attributes: true, attributeFilter: ['class', 'data-ac-motion'] });
  const removalObserver = new MutationObserver((records) => {
    for (const record of records) {
      for (const node of record.removedNodes) {
        if (node instanceof Element && !node.isConnected) destroyWithin(node);
      }
    }
    controllers.forEach((controller) => { if (!controller.connected) controller.destroy(); });
  });
  removalObserver.observe(document.body, { childList: true, subtree: true });
  const destroy = () => {
    if (lifecycle.signal.aborted) return;
    lifecycle.abort();
    motionObserver.disconnect();
    removalObserver.disconnect();
    if (activeAction) destroyAction(activeAction);
    destroyWithin(document.documentElement);
    for (const owner of pendingHoverOwners) {
      const state = actionStates.get(owner);
      if (state) clearPendingHover(owner, state);
    }
    controllers.forEach((controller) => controller.destroy());
    dataCache.clear();
    delete window.AcLottieManager;
  };
  on(motionQuery, 'change', syncMotion);

  on(document, 'pointerdown', onPointerDown, { capture: true, passive: true });
  on(document, 'pointermove', onPointerMove, { capture: true, passive: true });
  on(document, 'wheel', onScroll, { capture: true, passive: true });
  on(document, 'scroll', onScroll, { capture: true, passive: true });
  on(window, 'scroll', onScroll, { passive: true });
  on(document, 'pointerover', onPointerOver, { passive: true });
  on(document, 'pointerout', onPointerOut, { passive: true });
  on(document, 'pointerover', onChoicePointerOver, { passive: true });
  on(document, 'pointerout', onChoicePointerOut, { passive: true });
  on(document, 'keydown', onKeyDown, { capture: true });
  on(document, 'focusin', onFocusIn);
  on(document, 'focusout', onFocusOut);
  on(document, 'focusin', onChoiceFocusIn);
  on(document, 'focusout', onChoiceFocusOut);
  on(document, 'change', (event) => {
    if (!(event.target instanceof HTMLInputElement) || !event.target.matches('input[type="radio"], input[type="checkbox"]')) return;
    const group = event.target.closest('[data-ac-ref-option-group]') || event.target.parentElement;
    syncChoiceHovers(group || document);
  });
  on(document, 'ac:overlay-close', (event) => destroyWithin(event.detail?.panel));
  on(document, 'shopify:section:load', (event) => {
    syncChoiceHovers(event.target);
  });
  on(document, 'shopify:section:unload', (event) => destroyWithin(event.target));
  on(window, 'pagehide', (event) => {
    pageHidden = true;
    if (!event.persisted) { destroy(); return; }
    if (activeAction) destroyAction(activeAction);
    destroyWithin(document.documentElement);
    for (const owner of pendingHoverOwners) {
      const state = actionStates.get(owner);
      if (state) clearPendingHover(owner, state);
    }
    controllers.forEach((controller) => controller.destroy());
  });

  on(window, 'pageshow', (event) => {
    if (event.persisted) {
      pageHidden = false;
      syncChoiceHovers();
    }
  });

  window.AcLottieManager = Object.freeze({
    refresh: (root) => syncChoiceHovers(root),
    destroyWithin,
    destroy,
    assetUrl,
    create,
    loadData,
    loadRuntime,
    reducedMotion,
  });

  syncChoiceHovers();
})();
